/**
 * CanvasEditor Class
 *
 * The main orchestration class for canvas editing. Bundles all default behaviors
 * and provides a batteries-included editing experience.
 *
 * Usage (push-based data):
 * ```typescript
 * const editor = new CanvasEditor({
 *   container,
 *   canvas,
 *   createNode: (type, pos) => { ... },
 *   deleteNodes: (ids) => { ... },
 *   moveNodes: (ids, offset) => { ... },
 *   duplicateNodes: (ids, offset) => { ... return newIds; },
 * });
 * editor.setNodes(nodes);
 * editor.setEdges(edges);
 * ```
 *
 * Usage (callback-based data - backward compatible):
 * ```typescript
 * const editor = new CanvasEditor({
 *   container,
 *   getNodes: () => nodes,
 *   getEdges: () => edges,
 *   // ... same callbacks as above
 * });
 * ```
 */

import { createInputSystem, type InputSystem } from "@rool/input";
import {
  force2,
  flow,
  grid,
  sort,
  tree,
  align,
  distribute,
  type LayoutNode,
  type LayoutEdge,
  type Force2Options,
  type AlignType,
  type DistributeType,
} from "@rool/layout";
import { DirectCanvas } from "@rool/renderer";
import { fitToNodes as computeFitToNodes } from "@rool/renderer/geometry";
import type {
  Camera,
  WorldPoint,
  WorldRect,
  ViewportSize,
} from "@rool/renderer/types";
import type {
  ViewState,
  Point,
  EditorNode,
  EditorEdge,
  HitTestResult,
  ResizeHandlePosition,
  GuideSnapResult,
  ResizeGuideSnapResult,
} from "./types";
import { formatResizeHandleId, getAllResizeHandlePositions } from "./types";
import {
  getNodeEdges,
  findAlignmentGuides,
  findSpacingGuides,
} from "./guide-detection";
import {
  snapToGuides,
  snapResizeToGuides,
  GUIDE_SNAP_THRESHOLD_PX,
} from "./guide-snapping";
import * as viewState from "./view-state";
import {
  registerSelectionBehavior,
  registerDragBehavior,
  registerNodeOpsBehavior,
  registerResizeBehavior,
  registerConnectBehavior,
  registerRotateBehavior,
  registerClipboardBehavior,
  registerDropBehavior,
  type BehaviorContext,
  type DragBehaviorContext,
  type NodeOpsBehaviorContext,
  type ResizeBehaviorContext,
  type ConnectBehaviorContext,
  type RotateBehaviorContext,
  type ClipboardBehaviorContext,
  type DropBehaviorContext,
  type ClipboardData,
} from "./behaviors";
import { registerCameraBehavior } from "./behaviors/camera";
import { createZOrderManager, type ZOrderManager } from "./behaviors/z-order";
import { createCursorManager, type CursorManager } from "./behaviors/cursor";
import {
  createToolManager,
  type ToolManager,
  type ToolState,
  type ToolRegistry,
  type ToolId,
  type NodeTool,
} from "./tools";
import { createHitTest, hitTestEdges, type HitTestConfig } from "./hit-testing";
import { snapOffsetToGrid, snapPointToGrid } from "./grid-snapping";
import { createOverlayManager, type OverlayManager } from "./overlays";
import {
  defaultNodeToUINode,
  defaultEdgeToUIEdge,
  type NodeConverter,
  type EdgeConverter,
} from "./default-converters";
import {
  createNodeTypeRegistry,
  DEFAULT_NODE_TYPES,
  type NodeTypeRegistry,
  type NodeTypeDefinition,
} from "./node-types";
import type { CanvasNode, CanvasEdge, CanvasChangeEvent } from "./types";
import { createCameraAnimator, type CameraAnimator } from "./camera-animator";
import {
  createCameraAutomation,
  type CameraAutomation,
  type CameraAutomationConfig,
} from "./behaviors/camera-automation";
import { EditingManager, type EditingPlugin } from "./editing";
import { computeEditingCameraAdjustment } from "./editing-viewport";
import * as autofitSettings from "./autofit-settings";

// =============================================================================
// Options
// =============================================================================

export interface CanvasEditorOptions {
  /** Container element for input handling */
  container: HTMLElement;

  /** Optional InputSystem - if not provided, one will be created */
  input?: InputSystem;

  /**
   * Optional DirectCanvas instance. When provided, the editor will:
   * - Register camera behavior (zoom/pan/keyboard shortcuts)
   * - Own coordinate conversion (screenToWorld via canvas)
   * - Provide camera API (getCamera, setCamera, pan, zoom, panTo, zoomTo, etc.)
   *
   * When not provided, either `canvasOptions` or `screenToWorld` must be given.
   */
  canvas?: DirectCanvas;

  /**
   * Canvas configuration for internal DirectCanvas creation.
   * When provided (and `canvas` is not), the editor creates and owns a DirectCanvas.
   * The editor will destroy it on `destroy()`.
   */
  canvasOptions?: {
    minZoom?: number;
    maxZoom?: number;
    gridSize?: number;
    showGrid?: boolean;
    simplifyLOD?: boolean;
  };

  // =========================================================================
  // Camera Configuration (only used when canvas is provided)
  // =========================================================================

  /** Minimum zoom level (default: 0.1) — also settable via canvasOptions */
  minZoom?: number;

  /** Maximum zoom level (default: 5) — also settable via canvasOptions */
  maxZoom?: number;

  /** Called when camera changes (zoom/pan) */
  onCameraChange?: (camera: Camera) => void;

  // =========================================================================
  // Grid Configuration
  // =========================================================================

  /** Grid size for snapping (default: 20) */
  gridSize?: number;

  /** Enable grid snapping (default: true when canvas provided, false otherwise) */
  gridSnapping?: boolean;

  /** Enable alignment guides during drag (default: true when canvas provided) */
  guidesEnabled?: boolean;

  // =========================================================================
  // Data Layer (the "SDK" interface)
  // =========================================================================

  /**
   * Get all nodes.
   * Optional when using setNodes() to push data into the editor.
   * Required when the editor reads data from an external source.
   */
  getNodes?: () => EditorNode[];

  /**
   * Get all edges.
   * Optional when using setEdges() to push data into the editor.
   * Required when the editor reads data from an external source.
   */
  getEdges?: () => EditorEdge[];

  /** Create a node at position */
  createNode: (type: string, position: Point) => void;

  /** Delete nodes by ID */
  deleteNodes: (nodeIds: string[]) => void;

  /** Delete edges by ID */
  deleteEdges: (edgeIds: string[]) => void;

  /** Move nodes by offset */
  moveNodes: (nodeIds: string[], offset: Point) => void;

  /** Duplicate nodes at offset, returns new node IDs */
  duplicateNodes: (nodeIds: string[], offset: Point) => string[];

  /** Create an edge between two nodes */
  createEdge: (sourceNodeId: string, targetNodeId: string) => void;

  // =========================================================================
  // Clipboard Operations (all optional - sensible defaults provided)
  // =========================================================================

  /**
   * Create a node from clipboard data. Returns the new node ID.
   * This is the minimal callback needed for full paste support.
   * If not provided, paste will still work using duplicateNodes for same-session operations.
   * Can return a Promise for async node creation (e.g., SDK persistence).
   */
  createNodeWithData?: (
    nodeData: {
      type: string;
      size: { width: number; height: number };
      data?: unknown;
    },
    position: Point,
  ) => string | Promise<string>;

  /** Get full node data for copying (type and custom data). Default: extracts from node. */
  getNodeData?: (
    nodeId: string,
  ) => { type: string; data?: unknown } | undefined;

  /**
   * Paste nodes from clipboard data (advanced override).
   * If not provided, uses createNodeWithData or duplicateNodes.
   * Can return a Promise for async node creation.
   */
  pasteNodes?: (
    nodes: ClipboardData["nodes"],
    centerPosition: Point,
  ) => Map<string, string> | Promise<Map<string, string>>;

  /**
   * Paste edges from clipboard data (advanced override).
   * If not provided, uses createEdge.
   */
  pasteEdges?: (
    edges: ClipboardData["edges"],
    nodeIdMap: Map<string, string>,
  ) => void;

  // =========================================================================
  // External Content (for pasting text/images from outside the app)
  // =========================================================================

  /**
   * Called when the user pastes external content (image or text from outside the app).
   * The app should create the appropriate node via its SDK/backend.
   * Replaces the older createTextNode/createImageNode callbacks.
   */
  onExternalPaste?: (
    data:
      | { type: "image"; blob: Blob; position: Point }
      | { type: "text"; text: string; position: Point },
  ) => void;

  /**
   * Called when the user drops image files onto the canvas.
   * The app should upload and create image nodes via its SDK/backend.
   * If not provided, file drop is disabled.
   */
  onFileDrop?: (data: { files: File[]; position: Point }) => void;

  /** @deprecated Use onExternalPaste instead */
  createTextNode?: (text: string, position: Point) => string;

  /** @deprecated Use onExternalPaste instead */
  createImageNode?: (imageData: string | Blob, position: Point) => string;

  /**
   * Get human-readable label/title for a node (for clipboard text representation).
   * Default: extracts from node data (content, title, label), falls back to type.
   */
  getNodeLabel?: (nodeId: string) => string | undefined;

  /**
   * Get image blob for an image node (for copying to system clipboard).
   * Returns null if the node is not an image or the image can't be retrieved.
   * Default: returns null.
   */
  getNodeImageBlob?: (nodeId: string) => Promise<Blob | null>;

  // =========================================================================
  // Spatial Queries (provided by renderer/camera owner)
  // =========================================================================

  /**
   * Hit test at a world point.
   *
   * If not provided, a default hit test using bezier edges is created.
   * The default uses getNodes, getEdges, and getZoom for proper behavior.
   */
  hitTest?: (point: Point) => HitTestResult;

  /**
   * Get current zoom level (for zoom-adjusted edge hit testing threshold).
   * Only used if hitTest is not provided (for the default implementation).
   */
  getZoom?: () => number;

  /**
   * Hit test configuration (only used if hitTest is not provided).
   * Allows customizing edge style and threshold for the default hit test.
   */
  hitTestConfig?: HitTestConfig;

  /**
   * Convert screen coordinates to world coordinates.
   * Required when canvas is not provided. When canvas is provided,
   * this is auto-derived from canvas.screenToWorld (with client→screen conversion).
   */
  screenToWorld?: (screenX: number, screenY: number) => Point;

  // =========================================================================
  // Callbacks
  // =========================================================================

  /**
   * Unified change notification for all data mutations.
   * Called after every move, resize, rotate, delete, duplicate, etc.
   * All values are final (already snapped).
   * Use this for persistence (single switch statement).
   */
  onChange?: (event: CanvasChangeEvent) => void;

  /** Called when selection changes (node/edge IDs) */
  onSelectionChange?: (selectedIds: string[]) => void;

  /** Called when view state changes (advanced — prefer onSelectionChange) */
  onViewStateChange?: (state: ViewState) => void;

  /** Called when tool state changes */
  onToolChange?: (state: ToolState) => void;

  /** Called after layout finishes moving nodes (for persistence) */
  onLayoutComplete?: (movedNodeIds: string[]) => void;

  /** Optional logging callback */
  log?: (type: string, detail: string) => void;

  // =========================================================================
  // Rendering (optional - only when canvas is provided)
  // =========================================================================

  /**
   * Custom node converter for rendering.
   * Called to convert each CanvasNode for DirectCanvas rendering.
   * If not provided, uses a default converter.
   */
  convertNode?: NodeConverter;

  /**
   * Custom edge converter for rendering.
   * Called to convert each CanvasEdge for DirectCanvas rendering.
   * If not provided, uses a default converter.
   */
  convertEdge?: EdgeConverter;

  // =========================================================================
  // Configuration
  // =========================================================================

  /**
   * Editing plugins. When provided, the editor creates an EditingManager
   * and handles editing lifecycle automatically.
   * Register plugins for different node types (contentEditable, Tiptap, etc.).
   */
  editingPlugins?: EditingPlugin[];

  /**
   * Called when editing ends with content.
   * Used for SDK persistence of edited content.
   */
  onEditEnd?: (
    nodeId: string,
    content: string,
    size?: { width: number; height: number },
  ) => void;

  /**
   * Called when the node needs to be invalidated for re-rendering after editing.
   * Override to add custom cleanup (e.g., renderer cache invalidation).
   * Default: calls canvas.invalidateNode.
   */
  invalidateNodeAfterEdit?: (nodeId: string) => void;

  /**
   * Node type definitions for the type registry.
   * When provided, powers isResizable/shouldLockAspectRatio/getIntrinsicAspectRatio
   * automatically based on node type.
   * Default: includes header, sticky-note, markdown, image types.
   */
  nodeTypes?: NodeTypeDefinition[];

  /**
   * Get the type ID for a node. Used by the node type registry
   * to determine editing/resize policies.
   */
  getNodeType?: (nodeId: string) => string | undefined;

  /** Initial selection */
  initialSelection?: string[];

  /** Custom tools to register (in addition to defaults) */
  customTools?: NodeTool[];

  /** Enable connection handles for creating edges (default: true) */
  enableConnections?: boolean;

  /**
   * Connection mode:
   * - 'always': Handles shown on hover/selected nodes, drag from handle to connect
   * - 'tool-only': Handles only shown when connect tool is active, drag from anywhere on node
   * Default: 'always'
   */
  connectionMode?: "always" | "tool-only";

  // =========================================================================
  // Resize Configuration
  // =========================================================================

  /**
   * Callback to resize a node. If not provided, resize is disabled.
   */
  resizeNode?: (
    nodeId: string,
    bounds: { cx: number; cy: number; width: number; height: number },
  ) => void;

  /**
   * Minimum node size for resize (default: { width: 50, height: 50 })
   */
  minNodeSize?: { width: number; height: number };

  /**
   * Check if a node should have aspect ratio locked by default.
   * When true, the node resizes proportionally by default, and Shift unlocks it.
   * When false (default), the node resizes freely and Shift enables proportional.
   * Use this for images and other content that should maintain aspect ratio.
   */
  shouldLockAspectRatio?: (nodeId: string) => boolean;

  /**
   * Get actual rendered size for a node.
   * Returns the DOM-measured size if it differs from the model size.
   */
  getActualNodeSize?: (
    nodeId: string,
  ) => { width: number; height: number } | undefined;

  /**
   * Get grid size for snapping during resize preview.
   * Returns 0 or undefined if snapping is disabled.
   */
  getGridSize?: () => number;

  /**
   * Check if a node can be resized.
   * Returns false to disable resizing for specific node types.
   * Default: all nodes are resizable.
   */
  isResizable?: (nodeId: string) => boolean;

  /**
   * Get the intrinsic aspect ratio for a node (width / height).
   * Used for proportional resize to maintain the correct aspect ratio.
   * For images, this should return the actual image's aspect ratio, not the node's current size.
   * Returns undefined to use the node's current dimensions.
   */
  getIntrinsicAspectRatio?: (nodeId: string) => number | undefined;

  // =========================================================================
  // Content Measurement (for auto-sizing)
  // =========================================================================

  /**
   * Measure intrinsic content size for a node. Returns null if not supported.
   * Used by autoFitNode() to determine the correct size.
   */
  measureNodeContent?: (
    nodeId: string,
  ) => { width: number; height: number } | null;

  /**
   * Async version of measureNodeContent that waits for images to load.
   * Used by autoFitNodeAsync() for nodes with image content.
   */
  measureNodeContentAsync?: (
    nodeId: string,
  ) => Promise<{ width: number; height: number } | null>;

  // =========================================================================
  // Rotation Configuration
  // =========================================================================

  /**
   * Callback to rotate a node. If not provided, rotation is disabled.
   * Rotation is in radians.
   */
  rotateNode?: (nodeId: string, rotation: number) => void;

  /**
   * Get current rotation for a node (optional, falls back to node.rotation).
   * Rotation is in radians.
   */
  getNodeRotation?: (nodeId: string) => number;
}

// =============================================================================
// CanvasEditor Class
// =============================================================================

export class CanvasEditor {
  private state: ViewState;
  private readonly input: InputSystem;
  private readonly ownsInput: boolean;
  private readonly disconnectInput: () => void;
  private readonly behaviorCleanup: (() => void)[] = [];
  private connectBehaviorCleanup: (() => void) | null = null;
  private readonly toolManager: ToolManager;
  private readonly options: CanvasEditorOptions;
  private readonly hitTest: (point: Point) => HitTestResult;
  private readonly _canvas: DirectCanvas | null;
  private readonly _ownsCanvas: boolean;
  private readonly screenToWorldFn: (screenX: number, screenY: number) => Point;
  private readonly _zOrderManager: ZOrderManager | null;
  private readonly _cursorManager: CursorManager | null;
  private readonly _overlayManager: OverlayManager | null;
  private readonly _nodeConverter: NodeConverter;
  private readonly _edgeConverter: EdgeConverter;
  private readonly _nodeTypeRegistry: NodeTypeRegistry;

  private _connectionsEnabled: boolean;
  private _connectionMode: "always" | "tool-only";
  private lastCursorPosition: Point = { x: 0, y: 0 };
  private _cameraAnimator: CameraAnimator | null = null;
  private _cameraAutomation: CameraAutomation | null = null;
  private _editingManager: EditingManager | null = null;

  // Internal data store (used when getNodes/getEdges callbacks are not provided)
  private _nodes: EditorNode[] = [];
  private _edges: EditorEdge[] = [];

  // Grid snapping state
  private _gridSnapping: boolean;
  private readonly _gridSize: number;
  private _shiftHeldDuringDrag = false;

  // Alignment guide state
  private _guidesEnabled: boolean;
  private _activeGuides: GuideSnapResult | null = null;
  private _altHeldDuringDrag = false;

  constructor(options: CanvasEditorOptions) {
    this.options = options;
    this._connectionsEnabled = options.enableConnections ?? true;
    this._connectionMode = options.connectionMode ?? "tool-only";

    // Create or use provided DirectCanvas
    if (options.canvas) {
      this._canvas = options.canvas;
      this._ownsCanvas = false;
    } else if (options.canvasOptions) {
      this._canvas = new DirectCanvas(options.container, {
        minZoom: options.canvasOptions.minZoom ?? options.minZoom ?? 0.1,
        maxZoom: options.canvasOptions.maxZoom ?? options.maxZoom ?? 5,
        gridSize: options.canvasOptions.gridSize ?? options.gridSize ?? 20,
        showGrid: options.canvasOptions.showGrid ?? true,
        simplifyLOD: options.canvasOptions.simplifyLOD ?? false,
      });
      this._ownsCanvas = true;
    } else {
      this._canvas = null;
      this._ownsCanvas = false;
    }

    this._gridSize = options.canvasOptions?.gridSize ?? options.gridSize ?? 20;
    this._gridSnapping = options.gridSnapping ?? this._canvas !== null;
    this._guidesEnabled = options.guidesEnabled ?? this._canvas !== null;

    // Build screenToWorld function
    if (options.screenToWorld) {
      this.screenToWorldFn = options.screenToWorld;
    } else if (this._canvas) {
      const canvas = this._canvas;
      const container = options.container;
      this.screenToWorldFn = (screenX: number, screenY: number) => {
        const rect = container.getBoundingClientRect();
        const sx = screenX - rect.left;
        const sy = screenY - rect.top;
        return canvas.screenToWorld(sx, sy);
      };
    } else {
      throw new Error(
        "CanvasEditor: either screenToWorld callback, canvas, or canvasOptions must be provided",
      );
    }

    // Initialize state first (needed for hit test z-order)
    this.state = viewState.createInitialState(options.initialSelection);

    // Create hitTest function (use provided or create default)
    if (options.hitTest) {
      this.hitTest = options.hitTest;
    } else if (this._canvas) {
      // DOM-based hit test: uses actual rendered elements for nodes (more accurate
      // than bounding-box math), with edge hit testing via geometry
      const canvas = this._canvas;
      const container = options.container;
      const edgeThreshold = options.hitTestConfig?.edgeThreshold ?? 8;
      this.hitTest = (worldPoint: Point): HitTestResult => {
        const screenPt = canvas.worldToScreen(worldPoint.x, worldPoint.y);
        const rect = container.getBoundingClientRect();
        const nodeId = canvas.getNodeAtScreenPointDOM(
          screenPt.x + rect.left,
          screenPt.y + rect.top,
        );
        if (nodeId) {
          return { nodeId, edgeId: null, handleId: null, resizeHandleId: null };
        }
        const zoom = canvas.getCamera().zoom;
        const worldThreshold = edgeThreshold / zoom;
        const edgeId = hitTestEdges(
          this._getEdges(),
          this._getNodes(),
          worldPoint,
          worldThreshold,
        );
        return { nodeId: null, edgeId, handleId: null, resizeHandleId: null };
      };
    } else {
      // Bounding-box hit test (no DOM, for headless / test usage)
      this.hitTest = createHitTest({
        getNodes: () => this._getNodes(),
        getEdges: () => this._getEdges(),
        getZoom: options.getZoom,
        config: options.hitTestConfig,
      });
    }

    // Create or use provided InputSystem
    if (options.input) {
      this.input = options.input;
      this.ownsInput = false;
      // Assume caller already connected the input - don't connect again
      // (connecting twice would register duplicate event listeners)
      this.disconnectInput = () => {};
    } else {
      this.input = createInputSystem({ logLevel: "silent" });
      this.ownsInput = true;
      this.disconnectInput = this.input.connect(options.container);
    }

    // Register behaviors
    this.registerBehaviors();

    // Track cursor position for tool shortcuts
    this.registerCursorTracking();

    // Register camera behavior (when canvas is provided)
    if (this._canvas) {
      this.behaviorCleanup.push(
        registerCameraBehavior({
          input: this.input,
          canvas: this._canvas,
          container: options.container,
          config: {
            minZoom: options.minZoom ?? options.canvasOptions?.minZoom ?? 0.1,
            maxZoom: options.maxZoom ?? options.canvasOptions?.maxZoom ?? 5,
          },
          onCameraChange: (camera) => {
            options.onCameraChange?.(camera);
            // Re-render overlays since handle positions are in screen coords
            this._overlayManager?.refresh(this.state);
            this._overlayManager?.updateGuides(this._activeGuides);
            // Notify editing manager (for toolbar repositioning)
            this._editingManager?.onCameraChange();
          },
          log: options.log,
        }),
      );
    }

    // Register grid snapping shift-key override during drag
    if (this._gridSnapping) {
      this.registerGridSnappingShiftOverride();
    }

    // Register Alt key override for guide snapping
    if (this._guidesEnabled) {
      this.registerGuideAltOverride();
    }

    // Create z-order manager (when canvas is provided, editor manages z-order)
    this._zOrderManager = this._canvas
      ? createZOrderManager({
          getNodes: () => this._getNodes(),
          getViewState: () => this.state,
        })
      : null;

    // Create cursor manager (when canvas is provided, editor manages cursors)
    this._cursorManager = this._canvas
      ? createCursorManager({
          container: options.container,
          canvas: this._canvas,
          getNodeRotation: options.getNodeRotation,
        })
      : null;

    // Create overlay manager (when canvas is provided)
    this._overlayManager = this._canvas
      ? createOverlayManager({
          container: options.container,
          canvas: this._canvas,
          getNodes: () => this._getNodes(),
          getNodeRotation: options.getNodeRotation,
        })
      : null;

    // Set up converters
    this._nodeConverter = options.convertNode ?? defaultNodeToUINode;
    this._edgeConverter = options.convertEdge ?? defaultEdgeToUIEdge;

    // Set up node type registry
    this._nodeTypeRegistry = createNodeTypeRegistry();
    for (const typeDef of options.nodeTypes ?? DEFAULT_NODE_TYPES) {
      this._nodeTypeRegistry.register(typeDef);
    }

    // Setup tool manager
    this.toolManager = createToolManager({
      input: this.input,
      createNode: (type, position) => {
        const prevIds = new Set(this._getNodes().map((n) => n.id));
        options.createNode(type, position);
        this.nodesChanged();
        // Select the newly created node
        const newNode = this._getNodes().find((n) => !prevIds.has(n.id));
        if (newNode) {
          this.select([newNode.id]);
        }
      },
      getCursorPosition: () => this.lastCursorPosition,
      onToolChange: (state) => {
        // Update tool cursor on the cursor manager
        if (this._cursorManager) {
          const tool = state.activeTool
            ? this.toolManager.getRegistry().get(state.activeTool)
            : null;
          this._cursorManager.setToolCursor(tool?.cursorSvg ?? null);
          this._cursorManager.update(this.getViewState());
        }
        // Forward to app callback
        options.onToolChange?.(state);
      },
      tools: options.customTools,
    });

    // Initialize EditingManager (when plugins are provided and canvas is available)
    if (options.editingPlugins && this._canvas) {
      this._editingManager = new EditingManager({
        canvas: this._canvas,
        container: options.container,
        input: this.input,
        getNode: (nodeId) => {
          const node = this._getNodes().find((n) => n.id === nodeId);
          if (!node) return undefined;
          return {
            type: node.type,
            position: { ...node.position },
            size: { ...node.size },
            data: node.data,
          };
        },
        resizeNode: (nodeId, bounds) => {
          options.resizeNode?.(nodeId, bounds);
        },
        render: () => this.render(),
        onEditEnd: options.onEditEnd,
        invalidateNode: options.invalidateNodeAfterEdit,
        onStopRequested: (save: boolean) => this.handleEditStop(save),
        onResizeDuringEdit: (nodeId) => this.ensureEditingNodeVisible(nodeId),
      });
      for (const plugin of options.editingPlugins) {
        this._editingManager.registerPlugin(plugin);
      }
    }

    // Shift+C for locked connect tool
    this.behaviorCleanup.push(
      this.input.on(
        "keydown",
        "shift+c",
        {
          mode: "default",
          command: "Connect Tool (Locked)",
          category: "Tools",
        },
        (ctx) => {
          ctx.stopPropagation();
          this.activateTool("connect", true);
        },
      ),
    );
  }

  // ===========================================================================
  // Private: Internal Data Accessors
  // ===========================================================================

  /** Get nodes from callback (if provided) or internal store */
  private _getNodes(): EditorNode[] {
    return this.options.getNodes ? this.options.getNodes() : this._nodes;
  }

  /** Get edges from callback (if provided) or internal store */
  private _getEdges(): EditorEdge[] {
    return this.options.getEdges ? this.options.getEdges() : this._edges;
  }

  // ===========================================================================
  // Private: Behavior Registration
  // ===========================================================================

  private registerBehaviors(): void {
    const { options } = this;

    // Base context shared by all behaviors
    const baseContext: BehaviorContext = {
      input: this.input,
      getViewState: () => this.state,
      updateViewState: (newState) => this.updateState(newState),
      hitTest: this.hitTest,
      getNodes: () => this._getNodes(),
      getEdges: () => this._getEdges(),
      screenToWorld: this.screenToWorldFn,
      getZoom:
        options.getZoom ??
        (this._canvas ? () => this._canvas!.getCamera().zoom : undefined),
      log: options.log,
    };

    // Selection behavior
    this.behaviorCleanup.push(registerSelectionBehavior(baseContext));

    // Drag behavior
    // Wrap move/duplicate callbacks to apply grid snapping internally
    const snappedMoveNodes = (nodeIds: string[], offset: Point) => {
      let finalOffset = offset;

      if (this._guidesEnabled && this._activeGuides) {
        finalOffset = this._activeGuides.offset;
        // Grid snap only on axes not guide-snapped
        if (this._gridSnapping && !this._shiftHeldDuringDrag) {
          const gridSnapped = snapOffsetToGrid(
            nodeIds,
            this._getNodes(),
            finalOffset,
            this._gridSize,
          );
          if (!this._activeGuides.snappedAxes.x)
            finalOffset = { ...finalOffset, x: gridSnapped.x };
          if (!this._activeGuides.snappedAxes.y)
            finalOffset = { ...finalOffset, y: gridSnapped.y };
        }
      } else if (this._gridSnapping && !this._shiftHeldDuringDrag) {
        finalOffset = snapOffsetToGrid(
          nodeIds,
          this._getNodes(),
          offset,
          this._gridSize,
        );
      }

      options.moveNodes(nodeIds, finalOffset);
    };
    const snappedDuplicateNodes = (
      nodeIds: string[],
      offset: Point,
    ): string[] => {
      let finalOffset = offset;

      if (this._guidesEnabled && this._activeGuides) {
        finalOffset = this._activeGuides.offset;
        if (this._gridSnapping && !this._shiftHeldDuringDrag) {
          const gridSnapped = snapOffsetToGrid(
            nodeIds,
            this._getNodes(),
            finalOffset,
            this._gridSize,
          );
          if (!this._activeGuides.snappedAxes.x)
            finalOffset = { ...finalOffset, x: gridSnapped.x };
          if (!this._activeGuides.snappedAxes.y)
            finalOffset = { ...finalOffset, y: gridSnapped.y };
        }
      } else if (this._gridSnapping && !this._shiftHeldDuringDrag) {
        finalOffset = snapOffsetToGrid(
          nodeIds,
          this._getNodes(),
          offset,
          this._gridSize,
        );
      }

      const newIds = options.duplicateNodes(nodeIds, finalOffset);
      this.nodesChanged();
      this.edgesChanged();
      return newIds;
    };

    // Use getNodeData/createNodeWithData for type-preserving duplication if available
    const dragGetNodeData =
      options.getNodeData ?? this.defaultGetNodeData.bind(this);
    const dragContext: DragBehaviorContext = {
      ...baseContext,
      moveNodes: snappedMoveNodes,
      duplicateNodes: snappedDuplicateNodes,
      // Pass type-preserving callbacks if createNodeWithData is provided
      getNodeData: options.createNodeWithData
        ? (nodeId) => {
            const data = dragGetNodeData(nodeId);
            if (!data) return undefined;
            const node = this._getNodes().find((n) => n.id === nodeId);
            return {
              type: data.type,
              size: node?.size ?? { width: 200, height: 100 },
              data: data.data,
            };
          }
        : undefined,
      createNodeWithData: options.createNodeWithData,
      createEdge: (sourceNodeId: string, targetNodeId: string) => {
        options.createEdge(sourceNodeId, targetNodeId);
        this.edgesChanged();
      },
    };
    this.behaviorCleanup.push(registerDragBehavior(dragContext));

    // Node operations behavior
    const nodeOpsContext: NodeOpsBehaviorContext = {
      ...baseContext,
      createNode: (type: string, position: Point) => {
        const prevIds = new Set(this._getNodes().map((n) => n.id));
        options.createNode(type, position);
        this.nodesChanged();
        // Select the newly created node
        const newNode = this._getNodes().find((n) => !prevIds.has(n.id));
        if (newNode) {
          this.select([newNode.id]);
        }
      },
      deleteNodes: (nodeIds: string[]) => {
        options.deleteNodes(nodeIds);
        this.options.onChange?.({ type: "delete-nodes", nodeIds });
      },
      deleteEdges: (edgeIds: string[]) => {
        options.deleteEdges(edgeIds);
        this.options.onChange?.({ type: "delete-edges", edgeIds });
      },
      editNode: (nodeId: string) => this.handleEditRequest(nodeId),
      stopEditingNode: (save: boolean) => this.handleEditStop(save),
    };
    this.behaviorCleanup.push(registerNodeOpsBehavior(nodeOpsContext));

    // Clipboard behavior (always enabled with sensible defaults)
    const clipboardContext: ClipboardBehaviorContext = {
      ...baseContext,
      getNodeData: options.getNodeData ?? this.defaultGetNodeData.bind(this),
      pasteNodes: options.pasteNodes ?? this.defaultPasteNodes.bind(this),
      pasteEdges: options.pasteEdges ?? this.defaultPasteEdges.bind(this),
      deleteSelection: (nodeIds, edgeIds) => {
        options.deleteNodes(nodeIds);
        this.options.onChange?.({ type: "delete-nodes", nodeIds });
        options.deleteEdges(edgeIds);
        this.options.onChange?.({ type: "delete-edges", edgeIds });
      },
      getCursorPosition: () => this.lastCursorPosition,
      onExternalPaste: options.onExternalPaste,
      createTextNode: options.createTextNode,
      createImageNode: options.createImageNode,
      getNodeLabel: options.getNodeLabel ?? this.defaultGetNodeLabel.bind(this),
      getNodeImageBlob: options.getNodeImageBlob,
    };
    this.behaviorCleanup.push(registerClipboardBehavior(clipboardContext));

    // Drop behavior (only if onFileDrop callback provided)
    if (options.onFileDrop) {
      const dropContext: DropBehaviorContext = {
        ...baseContext,
        container: options.container,
        onFileDrop: options.onFileDrop,
      };
      this.behaviorCleanup.push(registerDropBehavior(dropContext));
    }

    // Resize behavior (only if resizeNode callback provided)
    if (options.resizeNode) {
      const resizeContext: ResizeBehaviorContext = {
        ...baseContext,
        resizeNode: options.resizeNode,
        minNodeSize: options.minNodeSize,
        shouldLockAspectRatio:
          options.shouldLockAspectRatio ??
          ((nodeId: string) => {
            const node = this._getNodes().find((n) => n.id === nodeId);
            return node?.type
              ? this._nodeTypeRegistry.isAspectRatioLocked(node.type)
              : false;
          }),
        getActualNodeSize:
          options.getActualNodeSize ??
          ((nodeId: string) => {
            const node = this._getNodes().find((n) => n.id === nodeId);
            return node?.size;
          }),
        getGridSize: options.getGridSize,
        isResizable:
          options.isResizable ??
          ((nodeId: string) => {
            const node = this._getNodes().find((n) => n.id === nodeId);
            return node?.type
              ? this._nodeTypeRegistry.isResizable(node.type)
              : true;
          }),
        getIntrinsicAspectRatio:
          options.getIntrinsicAspectRatio ??
          (this._canvas
            ? (nodeId: string) => {
                const node = this._getNodes().find((n) => n.id === nodeId);
                if (!node?.type) return undefined;
                const typeDef = this._nodeTypeRegistry.get(node.type);
                if (!typeDef?.getIntrinsicAspectRatio) return undefined;
                const element = this._canvas!.getNodeElement(nodeId);
                if (!element) return undefined;
                return typeDef.getIntrinsicAspectRatio(element);
              }
            : undefined),
      };
      this.behaviorCleanup.push(registerResizeBehavior(resizeContext));
    }

    // Rotate behavior (only if rotateNode callback provided)
    if (options.rotateNode) {
      const rotateContext: RotateBehaviorContext = {
        ...baseContext,
        rotateNode: options.rotateNode,
        getNodeRotation: options.getNodeRotation,
        isRotateToolActive: () =>
          this.toolManager.getState().activeTool === "rotate",
        onRotationComplete: () => {
          // Auto-exit tool mode unless locked
          this.toolManager.notifyToolUsed();
        },
      };
      this.behaviorCleanup.push(registerRotateBehavior(rotateContext));
    }

    // Connect behavior (registered separately for toggle support)
    if (this._connectionsEnabled) {
      this.registerConnectBehavior();
    }

    // Action button delegation: intercept clicks on [data-action] inside .canvas-node
    if (options.container && options.onChange) {
      const actionClickHandler = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const actionEl = target.closest?.(
          "[data-action]",
        ) as HTMLElement | null;
        if (!actionEl) return;
        const nodeEl = target.closest(".canvas-node") as HTMLElement | null;
        if (!nodeEl) return;
        const nodeId = nodeEl.dataset.nodeId;
        if (!nodeId) return;
        const action = actionEl.dataset.action;
        if (!action) return;
        e.stopPropagation();
        options.onChange!({ type: "action", nodeId, action });
      };
      options.container.addEventListener("click", actionClickHandler);
      this.behaviorCleanup.push(() => {
        options.container.removeEventListener("click", actionClickHandler);
      });
    }
  }

  private registerConnectBehavior(): void {
    if (this.connectBehaviorCleanup) return; // Already registered

    const { options } = this;
    const baseContext: BehaviorContext = {
      input: this.input,
      getViewState: () => this.state,
      updateViewState: (newState) => this.updateState(newState),
      hitTest: this.hitTest,
      getNodes: () => this._getNodes(),
      getEdges: () => this._getEdges(),
      screenToWorld: this.screenToWorldFn,
      log: options.log,
    };

    const connectContext: ConnectBehaviorContext = {
      ...baseContext,
      createEdge: (sourceNodeId: string, targetNodeId: string) => {
        options.createEdge(sourceNodeId, targetNodeId);
        this.options.onChange?.({
          type: "create-edge",
          sourceNodeId,
          targetNodeId,
        });
      },
      getNodeById: (nodeId) => this._getNodes().find((n) => n.id === nodeId),
      connectionMode: () => this._connectionMode,
      isConnectToolActive: () =>
        this.toolManager.getState().activeTool === "connect",
      onConnectionComplete: () => {
        // Deactivate connect tool after use (unless locked)
        const toolState = this.toolManager.getState();
        if (toolState.activeTool === "connect" && !toolState.locked) {
          this.toolManager.deactivate();
        }
      },
    };
    this.connectBehaviorCleanup = registerConnectBehavior(connectContext);
  }

  private unregisterConnectBehavior(): void {
    if (this.connectBehaviorCleanup) {
      this.connectBehaviorCleanup();
      this.connectBehaviorCleanup = null;
    }
  }

  private computeGuides(state: ViewState): GuideSnapResult | null {
    if (!state.drag || !this._canvas) return null;

    const allNodes = this._getNodes();
    const draggedIds = new Set(state.drag.nodes);

    // Filter out dragged nodes
    const candidates = allNodes.filter((n) => !draggedIds.has(n.id));

    // Compute raw drag offset
    const rawOffset = viewState.getDragOffset(state);
    if (!rawOffset) return null;

    // Compute dragged bounding box at raw offset position
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const nodeId of state.drag.nodes) {
      const node = allNodes.find((n) => n.id === nodeId);
      if (node) {
        minX = Math.min(minX, node.position.x + rawOffset.x);
        minY = Math.min(minY, node.position.y + rawOffset.y);
        maxX = Math.max(maxX, node.position.x + rawOffset.x + node.size.width);
        maxY = Math.max(maxY, node.position.y + rawOffset.y + node.size.height);
      }
    }

    const focusBounds = {
      cx: (minX + maxX) / 2,
      cy: (minY + maxY) / 2,
      width: maxX - minX,
      height: maxY - minY,
    };

    const visibleCandidates = this.getVisibleCandidates(
      candidates,
      focusBounds,
    );

    const draggedBounds = {
      top: minY,
      bottom: maxY,
      left: minX,
      right: maxX,
      centerX: focusBounds.cx,
      centerY: focusBounds.cy,
    };

    // Convert threshold from screen pixels to world units
    const camera = this._canvas.getCamera();
    const threshold = GUIDE_SNAP_THRESHOLD_PX / camera.zoom;

    // Detect guides
    const alignmentGuides = findAlignmentGuides(
      draggedBounds,
      visibleCandidates,
      threshold,
    );
    const spacingGuides = findSpacingGuides(
      draggedBounds,
      visibleCandidates,
      threshold,
    );

    // Snap to guides
    return snapToGuides(
      rawOffset,
      draggedBounds,
      alignmentGuides,
      spacingGuides,
      this._altHeldDuringDrag,
    );
  }

  private computeResizeGuides(state: ViewState): GuideSnapResult | null {
    if (!state.resize || !this._canvas) return null;

    // Skip guides for rotated nodes (axis-aligned matching is meaningless)
    if (state.resize.rotation !== 0) return null;

    const allNodes = this._getNodes();
    const resizingId = state.resize.nodeId;

    // Filter out the resizing node + get visible candidates (same as drag guides)
    const candidates = allNodes.filter((n) => n.id !== resizingId);
    const visibleCandidates = this.getVisibleCandidates(
      candidates,
      state.resize.currentBounds,
    );

    // Convert currentBounds to NodeEdges for guide detection
    const { cx, cy, width, height } = state.resize.currentBounds;
    const resizingBounds = {
      left: cx - width / 2,
      right: cx + width / 2,
      top: cy - height / 2,
      bottom: cy + height / 2,
      centerX: cx,
      centerY: cy,
    };

    const camera = this._canvas.getCamera();
    const threshold = GUIDE_SNAP_THRESHOLD_PX / camera.zoom;

    // Detect alignment guides (no spacing guides during resize)
    const alignmentGuides = findAlignmentGuides(
      resizingBounds,
      visibleCandidates,
      threshold,
    );

    // Snap resize bounds
    const result = snapResizeToGuides(
      state.resize.currentBounds,
      state.resize.handle,
      alignmentGuides,
      this._altHeldDuringDrag,
      this.options.minNodeSize,
    );

    // Apply snapped bounds back to resize state if adjusted
    if (result.snappedAxes.x || result.snappedAxes.y) {
      this.state.resize!.currentBounds = result.bounds;
    }

    // Return as GuideSnapResult shape for overlay rendering
    return {
      offset: { x: 0, y: 0 },
      guides: result.guides,
      spacingGuides: result.spacingGuides,
      snappedAxes: result.snappedAxes,
    };
  }

  /**
   * Get visible candidate nodes within the viewport (with margin).
   * Shared between drag and resize guide computation.
   */
  private getVisibleCandidates(
    candidates: EditorNode[],
    focus?: { cx: number; cy: number; width: number; height: number },
  ): EditorNode[] {
    if (!this._canvas) return candidates;

    const camera = this._canvas.getCamera();
    const container = this.options.container;
    const viewportW = container.clientWidth / camera.zoom;
    const viewportH = container.clientHeight / camera.zoom;
    const margin = Math.max(viewportW, viewportH) * 0.5;
    const worldRect = {
      x: camera.x - viewportW / 2 - margin,
      y: camera.y - viewportH / 2 - margin,
      width: viewportW + margin * 2,
      height: viewportH + margin * 2,
    };

    let visible = candidates.filter((n) => {
      const nx = n.position.x;
      const ny = n.position.y;
      const nr = nx + n.size.width;
      const nb = ny + n.size.height;
      return (
        nr > worldRect.x &&
        nx < worldRect.x + worldRect.width &&
        nb > worldRect.y &&
        ny < worldRect.y + worldRect.height
      );
    });

    // Cap at 100 candidates
    if (visible.length > 100 && focus) {
      visible.sort((a, b) => {
        const da =
          Math.abs(a.position.x + a.size.width / 2 - focus.cx) +
          Math.abs(a.position.y + a.size.height / 2 - focus.cy);
        const db =
          Math.abs(b.position.x + b.size.width / 2 - focus.cx) +
          Math.abs(b.position.y + b.size.height / 2 - focus.cy);
        return da - db;
      });
      visible = visible.slice(0, 100);
    }

    return visible;
  }

  private registerGridSnappingShiftOverride(): void {
    this.behaviorCleanup.push(
      this.input.on("keydown", "Shift", { mode: "drag" }, () => {
        this._shiftHeldDuringDrag = true;
      }),
    );
    this.behaviorCleanup.push(
      this.input.on("keyup", "Shift", { mode: "drag" }, () => {
        this._shiftHeldDuringDrag = false;
      }),
    );
  }

  private registerGuideAltOverride(): void {
    this.behaviorCleanup.push(
      this.input.on("keydown", "Alt", { mode: "drag" }, () => {
        this._altHeldDuringDrag = true;
        // Re-compute guides with alt held (disables snap)
        if (this.state.drag && this._guidesEnabled) {
          this._activeGuides = this.computeGuides(this.state);
          if (this._canvas) this.renderCanvas(this.state);
          this._overlayManager?.update(this.state);
        }
      }),
    );
    this.behaviorCleanup.push(
      this.input.on("keyup", "Alt", { mode: "drag" }, () => {
        this._altHeldDuringDrag = false;
        // Re-compute guides with alt released (enables snap)
        if (this.state.drag && this._guidesEnabled) {
          this._activeGuides = this.computeGuides(this.state);
          if (this._canvas) this.renderCanvas(this.state);
          this._overlayManager?.update(this.state);
        }
      }),
    );

    // Alt key during resize
    this.behaviorCleanup.push(
      this.input.on("keydown", "Alt", { mode: "resize" }, () => {
        this._altHeldDuringDrag = true;
        if (this.state.resize && this._guidesEnabled) {
          this._activeGuides = this.computeResizeGuides(this.state);
          if (this._canvas) this.renderCanvas(this.state);
          this._overlayManager?.update(this.state);
        }
      }),
    );
    this.behaviorCleanup.push(
      this.input.on("keyup", "Alt", { mode: "resize" }, () => {
        this._altHeldDuringDrag = false;
        if (this.state.resize && this._guidesEnabled) {
          this._activeGuides = this.computeResizeGuides(this.state);
          if (this._canvas) this.renderCanvas(this.state);
          this._overlayManager?.update(this.state);
        }
      }),
    );
  }

  private shouldSnap(): boolean {
    return this._gridSnapping && !this._shiftHeldDuringDrag;
  }

  private registerCursorTracking(): void {
    // Track cursor position for keyboard shortcuts (tool creation at cursor)
    const unsubscribe = this.input.on("mousemove", { mode: "*" }, (ctx) => {
      this.lastCursorPosition = this.screenToWorldFn(
        ctx.position!.x,
        ctx.position!.y,
      );

      // Update hover state
      const hit = this.hitTest(this.lastCursorPosition);

      if (hit.nodeId !== this.state.hovered.nodeId) {
        this.updateState(viewState.setHoveredNode(this.state, hit.nodeId));
      }

      if (!hit.nodeId && hit.edgeId !== this.state.hovered.edgeId) {
        this.updateState(viewState.setHoveredEdge(this.state, hit.edgeId));
      } else if (hit.nodeId && this.state.hovered.edgeId !== null) {
        this.updateState(viewState.setHoveredEdge(this.state, null));
      }
    });

    this.behaviorCleanup.push(unsubscribe);
  }

  private updateState(newState: ViewState): void {
    const prevState = this.state;
    this.state = newState;

    // Reset shift/alt overrides when drag or resize ends
    if (
      (prevState.drag && !newState.drag) ||
      (prevState.resize && !newState.resize)
    ) {
      this._shiftHeldDuringDrag = false;
      this._altHeldDuringDrag = false;
      this._activeGuides = null;
    }

    // Compute alignment guides during drag
    if (newState.drag && this._guidesEnabled && this._canvas) {
      this._activeGuides = this.computeGuides(newState);
    }

    // Compute alignment guides during resize
    if (newState.resize && this._guidesEnabled && this._canvas) {
      this._activeGuides = this.computeResizeGuides(newState);
    }

    // Disabled: bring-to-front on selection removed per design
    // if (this._zOrderManager) {
    // 	this._zOrderManager.bringToFrontOnSelection(prevState.selection, newState.selection);
    // }

    // Cursor: update based on view state
    if (this._cursorManager) {
      this._cursorManager.update(newState);
    }

    // Render to DirectCanvas (when canvas is available)
    if (this._canvas) {
      this.renderCanvas(newState);
    }

    // Overlays: update on state change
    if (this._overlayManager) {
      this._overlayManager.update(newState);
      this._overlayManager.updateGuides(this._activeGuides);
    }

    // Box selection rect visualization (when canvas is available)
    if (this._canvas) {
      if (newState.boxSelect && !prevState.boxSelect) {
        // Started box select
      } else if (!newState.boxSelect && prevState.boxSelect) {
        // Ended box select - hide rect
        this._canvas.hideSelectionRect();
      } else if (newState.boxSelect) {
        // Update box select rect
        const rect = viewState.getBoxSelectRect(newState);
        if (rect) {
          const topLeft = this._canvas.worldToScreen(rect.x, rect.y);
          const bottomRight = this._canvas.worldToScreen(
            rect.x + rect.width,
            rect.y + rect.height,
          );
          this._canvas.showSelectionRect({
            x: topLeft.x,
            y: topLeft.y,
            width: bottomRight.x - topLeft.x,
            height: bottomRight.y - topLeft.y,
          });
        }
      }
    }

    // === Unified onChange emissions for operation completion ===

    // Move completed (drag end, non-copying)
    if (prevState.drag && !newState.drag && !prevState.drag.copying) {
      if (this.options.onChange) {
        const draggedIds = prevState.drag.nodes;
        const nodes = this._getNodes();
        const movedNodes = draggedIds
          .map((id) => {
            const n = nodes.find((node) => node.id === id);
            return n
              ? { id: n.id, position: { x: n.position.x, y: n.position.y } }
              : null;
          })
          .filter((n): n is NonNullable<typeof n> => n !== null);
        if (movedNodes.length > 0) {
          this.options.onChange({ type: "move", nodes: movedNodes });
        }
      }
    }

    // Resize completed
    if (prevState.resize && !newState.resize) {
      if (this.options.onChange) {
        const nodeId = prevState.resize.nodeId;
        const node = this._getNodes().find((n) => n.id === nodeId);
        if (node) {
          this.options.onChange({
            type: "resize",
            nodeId,
            position: { x: node.position.x, y: node.position.y },
            size: { width: node.size.width, height: node.size.height },
          });
        }
      }
    }

    // Rotate completed
    if (prevState.rotate && !newState.rotate) {
      if (this.options.onChange) {
        const nodeId = prevState.rotate.nodeId;
        const node = this._getNodes().find((n) => n.id === nodeId);
        if (node) {
          this.options.onChange({
            type: "rotate",
            nodeId,
            rotation: node.rotation ?? 0,
          });
        }
      }
    }

    // Selection change
    if (prevState.selection !== newState.selection) {
      this.options.onSelectionChange?.(viewState.getSelectedIds(newState));
    }

    this.options.onViewStateChange?.(this.state);
  }

  /**
   * Render current state to DirectCanvas.
   * Converts nodes/edges via converters, adds ghost nodes for copy-drag,
   * and updates selection in DirectCanvas.
   */
  private renderCanvas(state: ViewState): void {
    if (!this._canvas) return;

    const nodes = this._getNodes();
    const edges = this._getEdges();
    const dragOffset =
      this._activeGuides?.offset ?? viewState.getDragOffset(state);
    const maxZ = this._zOrderManager?.getMaxZ() ?? 0;

    // Convert nodes for rendering
    const uiNodes: CanvasNode[] = nodes.map((n) =>
      this._nodeConverter(n, state, dragOffset),
    );

    // Add ghost nodes for copy-drag preview
    if (state.drag?.copying && dragOffset) {
      for (const nodeId of state.drag.nodes) {
        const original = nodes.find((n) => n.id === nodeId);
        if (original) {
          const ghostTopLeft = {
            x: original.position.x + dragOffset.x,
            y: original.position.y + dragOffset.y,
          };

          // Ghost node uses the original's type/data directly (CanvasNode has both)
          const ghostNode = this._nodeConverter(original, state, null);

          uiNodes.push({
            ...ghostNode,
            id: `ghost-${nodeId}`,
            type: original.type ?? ghostNode.type ?? "topic",
            position: ghostTopLeft,
            z: maxZ + 1000,
            size: original.size,
            data: original.data ?? ghostNode.data ?? {},
            className: "ghost",
            selected: false,
            hovered: false,
            dragging: false,
          });
        }
      }
    }

    // Convert edges for rendering
    const uiEdges: CanvasEdge[] = edges.map((e) =>
      this._edgeConverter(e, state),
    );

    // Add ghost edges for copy-drag preview
    if (state.drag?.copying && dragOffset) {
      const draggedNodeIds = new Set(state.drag.nodes);
      for (const edge of edges) {
        if (
          draggedNodeIds.has(edge.sourceNodeId) &&
          draggedNodeIds.has(edge.targetNodeId)
        ) {
          uiEdges.push({
            id: `ghost-${edge.id}`,
            type: "default",
            source: `ghost-${edge.sourceNodeId}`,
            target: `ghost-${edge.targetNodeId}`,
            data: {},
          });
        }
      }
    }

    this._canvas.render(uiNodes, uiEdges);

    // Update selection in DirectCanvas
    const nodeIdSet = new Set(nodes.map((n) => n.id));
    const selectedNodeIds = viewState
      .getSelectedIds(state)
      .filter((id) => nodeIdSet.has(id));
    this._canvas.selectNodes(selectedNodeIds);
  }

  // ===========================================================================
  // Public API: State Access
  // ===========================================================================

  /** Get current view state */
  getViewState(): ViewState {
    return this.state;
  }

  /** Get selected node/edge IDs */
  getSelection(): string[] {
    return viewState.getSelectedIds(this.state);
  }

  /** Check if a node/edge is selected */
  isSelected(id: string): boolean {
    return viewState.isSelected(this.state, id);
  }

  /** Check if currently dragging */
  isDragging(): boolean {
    return viewState.isDragging(this.state);
  }

  /** Check if currently editing */
  isEditing(): boolean {
    return viewState.isEditing(this.state);
  }

  /** Start editing a node (sets ViewState only — apps handle actual editing UI) */
  startEditing(nodeId: string, field: string = "content"): void {
    this.updateState(viewState.startEditing(this.state, nodeId, field));
  }

  /** Stop editing (clears ViewState only — apps handle actual editing UI cleanup) */
  stopEditing(): void {
    if (viewState.isEditing(this.state)) {
      this.updateState(viewState.stopEditing(this.state));
    }
  }

  /**
   * Start editing a node via the plugin system.
   * Uses the EditingManager to find the appropriate plugin and start editing.
   * Also sets ViewState editing state for proper mode handling.
   * Returns true if editing was started.
   */
  handleEditRequest(nodeId: string): boolean {
    if (!this._editingManager) return false;
    const started = this._editingManager.startEditing(nodeId);
    if (started && this.state.editing?.nodeId !== nodeId) {
      // Sync ViewState (for mode handling — prevents drag during editing)
      // Skip if already editing this node to avoid redundant re-render
      this.updateState(viewState.startEditing(this.state, nodeId, "content"));
    }
    return started;
  }

  /**
   * Stop editing via the plugin system.
   * Stops the active EditingManager session and clears ViewState.
   */
  handleEditStop(save: boolean): void {
    this._editingManager?.stopEditing(save);
    if (viewState.isEditing(this.state)) {
      this.updateState(viewState.stopEditing(this.state));
    }
  }

  /** Get the editing manager (for advanced usage) */
  getEditingManager(): EditingManager | null {
    return this._editingManager;
  }

  /**
   * Ensure the node being edited is fully visible in the viewport.
   * Called after each resize during editing (e.g., as a headline grows).
   * Pans or zooms the camera to keep the node in view.
   */
  private ensureEditingNodeVisible(nodeId: string): void {
    if (!this._canvas) return;

    const node = this._getNodes().find((n) => n.id === nodeId);
    if (!node) return;

    const camera = this._canvas.getCamera();
    const viewport = this._canvas.getViewport();
    const minZoom =
      this.options.minZoom ?? this.options.canvasOptions?.minZoom ?? 0.1;

    const adjustment = computeEditingCameraAdjustment({
      node: {
        x: node.position.x,
        y: node.position.y,
        width: node.size.width,
        height: node.size.height,
      },
      camera,
      viewport,
      minZoom,
    });

    if (adjustment) {
      this.animateCamera(adjustment);
    }
  }

  /** Check if currently connecting */
  isConnecting(): boolean {
    return viewState.isConnecting(this.state);
  }

  /** Get box selection rectangle (if active) */
  getBoxSelectRect() {
    return viewState.getBoxSelectRect(this.state);
  }

  /** Get drag offset (if dragging) */
  getDragOffset() {
    return viewState.getDragOffset(this.state);
  }

  /** Get connecting state (for rendering preview line) */
  getConnectingState() {
    return this.state.connecting;
  }

  /** Get hovered handle ID */
  getHoveredHandle(): string | null {
    return this.state.hovered.handleId;
  }

  /** Check if currently resizing */
  isResizing(): boolean {
    return viewState.isResizing(this.state);
  }

  /** Get current resize state */
  getResizeState(): ViewState["resize"] {
    return this.state.resize;
  }

  /** Get hovered resize handle ID */
  getHoveredResizeHandle(): string | null {
    return this.state.hovered.resizeHandleId;
  }

  /**
   * Get resize handle positions for a node.
   * Returns null if resize is not enabled or node not found.
   * Use this to render resize handles in the app.
   */
  getResizeHandlePositions(nodeId: string): Array<{
    id: string;
    position: ResizeHandlePosition;
    point: Point;
  }> | null {
    // Only return handles if resize is enabled
    if (!this.options.resizeNode) return null;

    const node = this._getNodes().find((n) => n.id === nodeId);
    if (!node) return null;

    return getAllResizeHandlePositions(node).map(({ position, point }) => ({
      id: formatResizeHandleId(nodeId, position),
      position,
      point,
    }));
  }

  /** Check if currently rotating */
  isRotating(): boolean {
    return viewState.isRotating(this.state);
  }

  /** Get current rotation state */
  getRotateState(): ViewState["rotate"] {
    return this.state.rotate;
  }

  /** Check if hovering over rotation zone */
  isHoveringRotationZone(): boolean {
    return this.state.hovered.rotationZone;
  }

  /** Check if rotate tool is currently active */
  isRotateToolActive(): boolean {
    return this.toolManager.getState().activeTool === "rotate";
  }

  // ===========================================================================
  // Public API: Data Sync
  // ===========================================================================

  /** Get all nodes */
  getNodes(): EditorNode[] {
    return this._getNodes();
  }

  /** Get all edges */
  getEdges(): EditorEdge[] {
    return this._getEdges();
  }

  /**
   * Push node data into the editor's internal store.
   * Use this instead of the `getNodes` callback for push-based data flow.
   * Triggers z-order sync and re-render.
   */
  setNodes(nodes: EditorNode[]): void {
    this._nodes = nodes;
    this._zOrderManager?.syncMaxZ();
    if (this._canvas) {
      this.renderCanvas(this.state);
      this._overlayManager?.refresh(this.state);
    }
  }

  /**
   * Push edge data into the editor's internal store.
   * Use this instead of the `getEdges` callback for push-based data flow.
   * Triggers re-render.
   */
  setEdges(edges: EditorEdge[]): void {
    this._edges = edges;
    if (this._canvas) {
      this.renderCanvas(this.state);
    }
  }

  /**
   * Notify the editor that nodes have changed (when using getNodes callback).
   * Updates z-order tracking and re-renders. Call this after changing the node list.
   */
  nodesChanged(): void {
    this._zOrderManager?.syncMaxZ();
    if (this._canvas) {
      this.renderCanvas(this.state);
      this._overlayManager?.refresh(this.state);
    }
  }

  /**
   * Notify the editor that edges have changed (when using getEdges callback).
   * Re-renders. Call this after changing the edge list.
   */
  edgesChanged(): void {
    if (this._canvas) {
      this.renderCanvas(this.state);
    }
  }

  /**
   * Force a full re-render.
   */
  render(): void {
    if (this._canvas) {
      this.renderCanvas(this.state);
      this._overlayManager?.refresh(this.state);
    }
  }

  /** Get the current max z-order (for rendering ghost nodes above all others) */
  getMaxZ(): number {
    return this._zOrderManager?.getMaxZ() ?? 0;
  }

  // ===========================================================================
  // Public API: Programmatic Control
  // ===========================================================================

  /** Select nodes/edges by ID */
  select(ids: string[]): void {
    this.updateState(viewState.applyMultiSelection(this.state, ids, "replace"));
  }

  /** Clear selection */
  clearSelection(): void {
    this.updateState(viewState.clearSelection(this.state));
  }

  /** Select all nodes and edges */
  selectAll(): void {
    const nodeIds = this._getNodes().map((n) => n.id);
    const edgeIds = this._getEdges().map((e) => e.id);
    console.warn(
      "[selectAll] nodes:",
      nodeIds.length,
      "edges:",
      edgeIds.length,
    );
    this.updateState(viewState.selectAll(this.state, [...nodeIds, ...edgeIds]));
  }

  // ===========================================================================
  // Public API: Connections
  // ===========================================================================

  /** Check if connections are enabled */
  get connectionsEnabled(): boolean {
    return this._connectionsEnabled;
  }

  /** Get current connection mode */
  get connectionMode(): "always" | "tool-only" {
    return this._connectionMode;
  }

  /** Set connection mode */
  setConnectionMode(mode: "always" | "tool-only"): void {
    this._connectionMode = mode;
  }

  /** Check if connect tool is currently active */
  isConnectToolActive(): boolean {
    return this.toolManager.getState().activeTool === "connect";
  }

  /** Enable or disable connection handles */
  setConnectionsEnabled(enabled: boolean): void {
    if (enabled === this._connectionsEnabled) return;

    this._connectionsEnabled = enabled;
    if (enabled) {
      this.registerConnectBehavior();
    } else {
      this.unregisterConnectBehavior();
      // Clear any hovered handle state
      if (this.state.hovered.handleId) {
        this.updateState(viewState.setHoveredHandle(this.state, null));
      }
    }
  }

  // ===========================================================================
  // Public API: Tool System
  // ===========================================================================

  /** Get current tool state */
  getToolState(): ToolState {
    return this.toolManager.getState();
  }

  /** Get tool registry */
  getToolRegistry(): ToolRegistry {
    return this.toolManager.getRegistry();
  }

  /** Activate a tool */
  activateTool(toolId: ToolId, locked: boolean = false): void {
    this.toolManager.activate(toolId, locked);
  }

  /** Deactivate current tool */
  deactivateTool(): void {
    this.toolManager.deactivate();
  }

  /** Handle toolbar click (with shift for lock) */
  handleToolbarClick(toolId: ToolId, shiftKey: boolean): void {
    this.toolManager.handleToolbarClick(toolId, shiftKey);
  }

  // ===========================================================================
  // Public API: Input System Access
  // ===========================================================================

  /** Get the input system (for additional handlers or inspection) */
  getInputSystem(): InputSystem {
    return this.input;
  }

  // ===========================================================================
  // Default Clipboard Implementations
  // ===========================================================================

  /** Default getNodeData - extracts type from node if available */
  private defaultGetNodeData(
    nodeId: string,
  ): { type: string; data?: unknown } | undefined {
    const node = this._getNodes().find((n) => n.id === nodeId);
    if (!node) return undefined;
    return { type: node.type, data: node.data };
  }

  /** Default pasteNodes - uses createNodeWithData if available */
  private async defaultPasteNodes(
    clipboardNodes: ClipboardData["nodes"],
    centerPosition: Point,
  ): Promise<Map<string, string>> {
    const idMap = new Map<string, string>();
    const { createNodeWithData, log } = this.options;

    log?.(
      "defaultPasteNodes",
      `${clipboardNodes.length} nodes, createNodeWithData: ${!!createNodeWithData}`,
    );

    if (!createNodeWithData) {
      // No way to create nodes - paste won't work
      log?.("paste-error", "No createNodeWithData callback provided");
      return idMap;
    }

    for (const clipNode of clipboardNodes) {
      // Calculate position (clipNode.relativePosition is relative to selection center)
      const position = {
        x: centerPosition.x + clipNode.relativePosition.x,
        y: centerPosition.y + clipNode.relativePosition.y,
      };

      log?.(
        "defaultPasteNodes-create",
        `Creating node at (${position.x.toFixed(0)}, ${position.y.toFixed(0)})`,
      );
      // Support both sync and async createNodeWithData
      const newId = await Promise.resolve(
        createNodeWithData(
          { type: clipNode.type, size: clipNode.size, data: clipNode.data },
          position,
        ),
      );
      log?.(
        "defaultPasteNodes-created",
        `oldId: ${clipNode.id} -> newId: ${newId}`,
      );
      idMap.set(clipNode.id, newId);
    }

    return idMap;
  }

  /** Default pasteEdges - uses createEdge */
  private defaultPasteEdges(
    clipboardEdges: ClipboardData["edges"],
    nodeIdMap: Map<string, string>,
  ): void {
    const { createEdge } = this.options;

    for (const clipEdge of clipboardEdges) {
      const newSourceId = nodeIdMap.get(clipEdge.sourceNodeId);
      const newTargetId = nodeIdMap.get(clipEdge.targetNodeId);

      if (newSourceId && newTargetId) {
        createEdge(newSourceId, newTargetId);
      }
    }
  }

  /** Default getNodeLabel - extracts from node data fields, falls back to type */
  private defaultGetNodeLabel(nodeId: string): string | undefined {
    const node = this._getNodes().find((n) => n.id === nodeId);
    if (!node) return undefined;

    // Try label field on node
    const label = (node as { label?: string }).label;
    if (typeof label === "string" && label) return label;

    // Try common data fields (supports both flat and nested obj structures)
    const data = node.data as Record<string, unknown> | undefined;
    if (data) {
      const obj = data.obj as Record<string, unknown> | undefined;
      if (obj) {
        for (const key of ["content", "title", "label"]) {
          if (typeof obj[key] === "string" && obj[key])
            return obj[key] as string;
        }
      }
      for (const key of ["content", "title", "label"]) {
        if (typeof data[key] === "string" && data[key])
          return data[key] as string;
      }
    }

    return node.type;
  }

  // ===========================================================================
  // Private: Internal Content Measurement
  // ===========================================================================

  /**
   * Measure node content using the internal canvas.
   * Converts the node via the built-in converter before measuring.
   */
  private _measureNodeContent(
    nodeId: string,
    extraOptions?: { fixedWidth?: number },
  ): { width: number; height: number } | null {
    if (!this._canvas) return null;
    const node = this._getNodes().find((n) => n.id === nodeId);
    if (!node) return null;
    const uiNode = this._nodeConverter(node, this.state, null);
    return this._canvas.measureNodeContent(uiNode as any, extraOptions);
  }

  /**
   * Async measure node content using the internal canvas (waits for images).
   * Converts the node via the built-in converter before measuring.
   */
  private async _measureNodeContentAsync(
    nodeId: string,
    extraOptions?: { fixedWidth?: number },
  ): Promise<{ width: number; height: number } | null> {
    if (!this._canvas) return null;
    const node = this._getNodes().find((n) => n.id === nodeId);
    if (!node) return null;
    const uiNode = this._nodeConverter(node, this.state, null);
    return this._canvas.measureNodeContentAsync(uiNode as any, extraOptions);
  }

  // ===========================================================================
  // Public API: Content Measurement
  // ===========================================================================

  /**
   * Measure intrinsic content size for a node.
   * Uses the internal canvas and node converter. Returns null if not available.
   */
  measureNodeContent(nodeId: string): { width: number; height: number } | null {
    if (this.options.measureNodeContent) {
      return this.options.measureNodeContent(nodeId);
    }
    return this._measureNodeContent(nodeId);
  }

  /**
   * Async measure intrinsic content size for a node, waiting for images to load.
   * Uses the internal canvas and node converter. Returns null if not available.
   */
  async measureNodeContentAsync(
    nodeId: string,
  ): Promise<{ width: number; height: number } | null> {
    if (this.options.measureNodeContentAsync) {
      return this.options.measureNodeContentAsync(nodeId);
    }
    return this._measureNodeContentAsync(nodeId);
  }

  // ===========================================================================
  // Public API: Camera (requires canvas)
  // ===========================================================================

  /** Get current camera state. Requires canvas. */
  getCamera(): Camera {
    if (!this._canvas) throw new Error("getCamera requires canvas");
    return this._canvas.getCamera();
  }

  /** Set camera state. Requires canvas. */
  setCamera(camera: Partial<Camera>, _animate = false): void {
    if (!this._canvas) throw new Error("setCamera requires canvas");
    const current = this._canvas.getCamera();
    this._canvas.setCamera({
      x: camera.x ?? current.x,
      y: camera.y ?? current.y,
      zoom: camera.zoom ?? current.zoom,
    });
    this.options.onCameraChange?.(this._canvas.getCamera());
  }

  /** Pan by screen-space delta. Requires canvas. */
  pan(deltaX: number, deltaY: number): void {
    if (!this._canvas) throw new Error("pan requires canvas");
    this._canvas.pan(deltaX, deltaY);
    this.options.onCameraChange?.(this._canvas.getCamera());
  }

  /** Pan camera to center on a world point. Requires canvas. */
  panTo(worldX: number, worldY: number): void {
    if (!this._canvas) throw new Error("panTo requires canvas");
    this._canvas.panTo(worldX, worldY);
    this.options.onCameraChange?.(this._canvas.getCamera());
  }

  /** Zoom by factor around optional screen-space origin. Requires canvas. */
  zoom(factor: number, originX?: number, originY?: number): void {
    if (!this._canvas) throw new Error("zoom requires canvas");
    const viewport = this._canvas.getViewport();
    const ox = originX ?? viewport.width / 2;
    const oy = originY ?? viewport.height / 2;
    this._canvas.zoom(factor, ox, oy);
    this.options.onCameraChange?.(this._canvas.getCamera());
  }

  /** Zoom to a specific level, optionally focusing on a world point. Requires canvas. */
  zoomTo(zoom: number, focusX?: number, focusY?: number): void {
    if (!this._canvas) throw new Error("zoomTo requires canvas");
    this._canvas.zoomTo(zoom, focusX, focusY);
    this.options.onCameraChange?.(this._canvas.getCamera());
  }

  /** Reset camera to origin at 100% zoom. Requires canvas. */
  resetView(): void {
    if (!this._canvas) throw new Error("resetView requires canvas");
    this._canvas.setCamera({ x: 0, y: 0, zoom: 1 });
    this.options.onCameraChange?.(this._canvas.getCamera());
    this.options.log?.("view-reset", "");
  }

  /** Fit camera to show specified nodes (or all). Requires canvas. */
  fitToNodes(
    nodeIds?: string[],
    padding?: number,
    options?: { minZoom?: number; maxZoom?: number },
  ): void {
    if (!this._canvas) throw new Error("fitToNodes requires canvas");

    if (options?.minZoom !== undefined || options?.maxZoom !== undefined) {
      // Compute fit with custom zoom limits
      const nodes = this._getNodes();
      const nodeArray = nodeIds
        ? nodes.filter((n) => nodeIds.includes(n.id))
        : nodes;
      if (nodeArray.length === 0) return;

      const viewport = this._canvas.getViewport();
      const result = computeFitToNodes(
        nodeArray.map((n) => ({ position: n.position, size: n.size })),
        viewport,
        padding ?? 40,
        options.minZoom ?? 0.1,
        options.maxZoom ?? 5,
      );
      if (result) {
        this._canvas.setCamera(result);
      }
    } else {
      this._canvas.fitToNodes(nodeIds, padding);
    }

    this.options.onCameraChange?.(this._canvas.getCamera());
    this.options.log?.(
      "fit-to-content",
      `zoom: ${(this._canvas.getCamera().zoom * 100).toFixed(0)}%`,
    );
  }

  /** Animate camera to a target position/zoom. Requires canvas. */
  animateCamera(target: Camera): void {
    if (!this._canvas) throw new Error("animateCamera requires canvas");
    if (!this._cameraAnimator) {
      const canvas = this._canvas;
      const options = this.options;
      const overlayManager = this._overlayManager;
      const state = () => this.state;
      const editingManager = this._editingManager;
      this._cameraAnimator = createCameraAnimator({
        getCamera: () => canvas.getCamera(),
        setCamera: (camera) => {
          canvas.setCamera(camera);
          options.onCameraChange?.(camera);
          overlayManager?.refresh(state());
          // Reposition editing toolbar during camera animation
          editingManager?.onCameraChange();
        },
      });
    }
    this._cameraAnimator.animateTo(target);
  }

  /** Animate camera to fit specified nodes (or all) with padding. Requires canvas.
   *  Viewport-aware: prefers panning over zooming in, only zooms in gently for
   *  tiny nodes, and zooms out freely when content doesn't fit. */
  animateFitToNodes(nodeIds?: string[], padding?: number): void {
    if (!this._canvas) throw new Error("animateFitToNodes requires canvas");

    const nodes = this._getNodes();
    const targetNodes = nodeIds
      ? nodes.filter((n) => nodeIds.includes(n.id))
      : nodes;

    if (targetNodes.length === 0) return;

    const viewport = this._canvas.getViewport();
    const pad = padding ?? 50;
    const minZoom =
      this.options.minZoom ?? this.options.canvasOptions?.minZoom ?? 0.1;
    const maxZoom =
      this.options.maxZoom ?? this.options.canvasOptions?.maxZoom ?? 5;

    const naiveTarget = computeFitToNodes(
      targetNodes.map((n) => ({ position: n.position, size: n.size })),
      viewport,
      pad,
      minZoom,
      maxZoom,
    );

    const currentCamera = this._canvas.getCamera();
    let target: Camera;

    if (naiveTarget.zoom > currentCamera.zoom) {
      // Fit wants to zoom in — be gentle
      const maxScreenDim = Math.max(
        ...targetNodes.map(
          (n) => Math.max(n.size.width, n.size.height) * currentCamera.zoom,
        ),
      );

      if (maxScreenDim < 60) {
        // Nodes are tiny at current zoom — zoom in gently
        const largestWorldDim = Math.max(
          ...targetNodes.map((n) => Math.max(n.size.width, n.size.height)),
        );
        const idealZoom = 150 / largestWorldDim;
        const maxGentleZoom = currentCamera.zoom * 1.3;
        const gentleZoom = Math.min(idealZoom, maxGentleZoom, naiveTarget.zoom);
        target = {
          x: naiveTarget.x,
          y: naiveTarget.y,
          zoom: Math.max(gentleZoom, currentCamera.zoom),
        };
      } else {
        // Nodes are visible at current zoom — just pan, no zoom in
        target = {
          x: naiveTarget.x,
          y: naiveTarget.y,
          zoom: currentCamera.zoom,
        };
      }
    } else if (naiveTarget.zoom < currentCamera.zoom) {
      // Fit wants to zoom out — allow it with breathing room
      target = {
        ...naiveTarget,
        zoom: Math.max(naiveTarget.zoom * 0.85, minZoom),
      };
    } else {
      // Same zoom — just pan
      target = naiveTarget;
    }

    this.animateCamera(target);
  }

  /** Stop any in-progress camera animation. */
  stopCameraAnimation(): void {
    this._cameraAnimator?.stop();
  }

  /** Check if a camera animation is currently running. */
  isCameraAnimating(): boolean {
    return this._cameraAnimator?.isAnimating() ?? false;
  }

  // ===========================================================================
  // Public API: Camera Automation
  // ===========================================================================

  /**
   * Get the camera automation controller.
   * Lazily created on first access. Requires canvas.
   */
  get cameraAutomation(): CameraAutomation {
    if (!this._canvas) throw new Error("cameraAutomation requires canvas");

    if (!this._cameraAutomation) {
      const canvas = this._canvas;
      const options = this.options;
      const overlayManager = this._overlayManager;
      const getState = () => this.state;

      this._cameraAutomation = createCameraAutomation({
        input: this.input,
        getViewState: getState,
        getNodes: () => this._getNodes(),
        getCamera: () => canvas.getCamera(),
        setCamera: (camera) => {
          canvas.setCamera(camera);
          options.onCameraChange?.(camera);
          overlayManager?.refresh(getState());
        },
        getViewport: () => canvas.getViewport(),
        minZoom: options.minZoom ?? options.canvasOptions?.minZoom ?? 0.1,
        maxZoom: options.maxZoom ?? options.canvasOptions?.maxZoom ?? 5,
        log: options.log,
      });
    }

    return this._cameraAutomation;
  }

  // ===========================================================================
  // Public API: Coordinate Conversion (requires canvas)
  // ===========================================================================

  /**
   * Convert container-relative screen coordinates to world coordinates.
   * Same as DirectCanvas.screenToWorld — no hidden offsets.
   */
  screenToWorld(screenX: number, screenY: number): WorldPoint {
    if (!this._canvas) throw new Error("screenToWorld requires canvas");
    return this._canvas.screenToWorld(screenX, screenY);
  }

  /**
   * Convert world coordinates to container-relative screen coordinates.
   * Same as DirectCanvas.worldToScreen.
   */
  worldToScreen(worldX: number, worldY: number): WorldPoint {
    if (!this._canvas) throw new Error("worldToScreen requires canvas");
    return this._canvas.worldToScreen(worldX, worldY);
  }

  /** Get viewport dimensions. Requires canvas. */
  getViewport(): ViewportSize {
    if (!this._canvas) throw new Error("getViewport requires canvas");
    return this._canvas.getViewport();
  }

  /** Get visible world rect (frustum). Requires canvas. */
  getVisibleWorldRect(): WorldRect {
    if (!this._canvas) throw new Error("getVisibleWorldRect requires canvas");
    return this._canvas.getVisibleWorldRect();
  }

  // ===========================================================================
  // Public API: Canvas Access
  // ===========================================================================

  /** Get the DirectCanvas instance (or null if not provided) */
  getCanvas(): DirectCanvas | null {
    return this._canvas;
  }

  // ===========================================================================
  // Public API: Grid Snapping
  // ===========================================================================

  /** Enable or disable grid snapping */
  setGridSnapping(enabled: boolean): void {
    this._gridSnapping = enabled;
    this.options.log?.("grid-snapping", enabled ? "enabled" : "disabled");
  }

  /** Check if grid snapping is enabled */
  getGridSnapping(): boolean {
    return this._gridSnapping;
  }

  /** Get the grid size */
  getGridSize(): number {
    return this._gridSize;
  }

  /** Enable or disable alignment guides */
  setGuidesEnabled(enabled: boolean): void {
    this._guidesEnabled = enabled;
    if (!enabled) this._activeGuides = null;
    this.options.log?.("guides", enabled ? "enabled" : "disabled");
  }

  /** Check if alignment guides are enabled */
  getGuidesEnabled(): boolean {
    return this._guidesEnabled;
  }

  /** Get active guide snap result (for overlay rendering) */
  getActiveGuides(): GuideSnapResult | null {
    return this._activeGuides;
  }

  // ===========================================================================
  // Public API: Node Type Registry
  // ===========================================================================

  /** Get the node type registry */
  getNodeTypeRegistry(): NodeTypeRegistry {
    return this._nodeTypeRegistry;
  }

  // ===========================================================================
  // Public API: Auto-Fit
  // ===========================================================================

  /** Auto-fit a node to its content. Returns true if resized. */
  autoFitNode(nodeId: string): boolean {
    const { resizeNode } = this.options;
    if (!resizeNode) return false;

    const node = this._getNodes().find((n) => n.id === nodeId);
    if (!node) return false;

    // Read autofit settings
    const settings = autofitSettings.getAutoFitSettings(node as any);

    // autoSizeText mode or both autofit axes off → skip
    if (settings.autoSizeText) return false;
    if (!settings.autoFitWidth && !settings.autoFitHeight) return false;

    // Build measurement options
    const extraOptions: { fixedWidth?: number } = {};
    if (settings.autoFitHeight && !settings.autoFitWidth) {
      // Height-only: keep current width, measure height at that width
      extraOptions.fixedWidth = node.size.width;
    }

    // Use provided measurer or fall back to internal canvas measurement
    const size = this.options.measureNodeContent
      ? this.options.measureNodeContent(nodeId)
      : this._measureNodeContent(nodeId, extraOptions);
    if (!size) return false;

    // Apply only the axes that are enabled
    const newWidth = settings.autoFitWidth ? size.width : node.size.width;
    const newHeight = settings.autoFitHeight ? size.height : node.size.height;

    // Skip if size hasn't meaningfully changed
    if (
      Math.abs(newWidth - node.size.width) < 2 &&
      Math.abs(newHeight - node.size.height) < 2
    )
      return false;

    // Compute center (EditorNode.position is top-left)
    const cx = node.position.x + node.size.width / 2;
    const cy = node.position.y + node.size.height / 2;

    // Resize preserving center position (resizeNode uses center-based bounds)
    resizeNode(nodeId, { cx, cy, width: newWidth, height: newHeight });
    return true;
  }

  /** Auto-fit multiple nodes. Returns IDs of nodes that were resized. */
  autoFitNodes(nodeIds: string[]): string[] {
    const resized = nodeIds.filter((id) => this.autoFitNode(id));
    if (resized.length > 0) {
      this.render();
    }
    return resized;
  }

  /** Async auto-fit a node, waiting for images to load. Returns true if resized. */
  async autoFitNodeAsync(nodeId: string): Promise<boolean> {
    const { resizeNode } = this.options;
    if (!resizeNode) return false;

    const node = this._getNodes().find((n) => n.id === nodeId);
    if (!node) return false;

    // Read autofit settings
    const settings = autofitSettings.getAutoFitSettings(node as any);

    // autoSizeText mode or both autofit axes off → skip
    if (settings.autoSizeText) return false;
    if (!settings.autoFitWidth && !settings.autoFitHeight) return false;

    // Build measurement options
    const extraOptions: { fixedWidth?: number } = {};
    if (settings.autoFitHeight && !settings.autoFitWidth) {
      extraOptions.fixedWidth = node.size.width;
    }

    // Prefer provided async measurer, fall back to internal, then sync
    const size = this.options.measureNodeContentAsync
      ? await this.options.measureNodeContentAsync(nodeId)
      : await this._measureNodeContentAsync(nodeId, extraOptions);

    if (!size) {
      return this.autoFitNode(nodeId);
    }

    // Apply only the axes that are enabled
    const newWidth = settings.autoFitWidth ? size.width : node.size.width;
    const newHeight = settings.autoFitHeight ? size.height : node.size.height;

    // Skip if size hasn't meaningfully changed
    if (
      Math.abs(newWidth - node.size.width) < 2 &&
      Math.abs(newHeight - node.size.height) < 2
    )
      return false;

    // Compute center (EditorNode.position is top-left)
    const cx = node.position.x + node.size.width / 2;
    const cy = node.position.y + node.size.height / 2;

    // Resize preserving center position
    resizeNode(nodeId, { cx, cy, width: newWidth, height: newHeight });
    return true;
  }

  /** Async auto-fit multiple nodes. Returns IDs of nodes that were resized. */
  async autoFitNodesAsync(nodeIds: string[]): Promise<string[]> {
    const results = await Promise.all(
      nodeIds.map(async (id) => ({
        id,
        resized: await this.autoFitNodeAsync(id),
      })),
    );
    const resized = results.filter((r) => r.resized).map((r) => r.id);
    if (resized.length > 0) {
      this.render();
    }
    return resized;
  }

  // ===========================================================================
  // Public API: Layout
  // ===========================================================================

  /**
   * Apply layout to the current selection.
   *
   * Algorithm choices:
   * - `'auto'` (default): force2 for connected graphs, grid for disconnected
   * - `'force2'`: always use force-directed layout
   * - `'sort'`: masonry/gallery-wall arrangement
   * - `'grid'`: uniform grid arrangement
   * - `'flow'`: DAG rank-based flow layout
   * - `'tree'`: hierarchical tree layout
   *
   * Uses actual rendered sizes (via getActualNodeSize) when available.
   * Non-selected nodes act as obstacles (force2 only).
   */
  layoutSelection(
    options?: Partial<Force2Options> & {
      algorithm?: "auto" | "force2" | "sort" | "grid" | "flow" | "tree";
    },
  ): void {
    const algorithm = options?.algorithm ?? "auto";
    let selectedIds = this.getSelection();
    const allNodes = this._getNodes();
    const allEdges = this._getEdges();
    const { log, moveNodes } = this.options;
    const getActualNodeSize =
      this.options.getActualNodeSize ??
      ((nodeId: string) => this._getNodes().find((n) => n.id === nodeId)?.size);

    // Filter to only selected nodes (selection may include edge IDs)
    const nodeIdSet = new Set(allNodes.map((n) => n.id));
    let selectedNodeIds = selectedIds.filter((id) => nodeIdSet.has(id));

    // If nothing or insufficient selected, use all nodes for layout WITHOUT changing selection
    if (selectedNodeIds.length < 2 && allNodes.length >= 2) {
      selectedNodeIds = allNodes.map((n) => n.id);
    }

    if (selectedNodeIds.length < 2) {
      log?.(
        "layout",
        `skipped: only ${selectedNodeIds.length} nodes selected (need 2+)`,
      );
      return;
    }
    const selectedNodeSet = new Set(selectedNodeIds);

    // Helper: get node size, preferring actual rendered size
    const getNodeSize = (node: EditorNode) => {
      const actual = getActualNodeSize?.(node.id);
      return actual ?? node.size;
    };

    // Build layout nodes from selected nodes
    const layoutNodes: LayoutNode[] = [];
    for (const node of allNodes) {
      if (!selectedNodeSet.has(node.id)) continue;
      const size = getNodeSize(node);
      layoutNodes.push({
        id: node.id,
        shape: {
          type: "rect",
          bounds: {
            x: node.position.x,
            y: node.position.y,
            width: size.width,
            height: size.height,
          },
        },
      });
    }

    // Edges between selected nodes
    const layoutEdges: LayoutEdge[] = [];
    for (const edge of allEdges) {
      if (
        selectedNodeSet.has(edge.sourceNodeId) &&
        selectedNodeSet.has(edge.targetNodeId)
      ) {
        layoutEdges.push({
          source: edge.sourceNodeId,
          target: edge.targetNodeId,
        });
      }
    }

    // Check if the selected subgraph is connected
    const isConnected = this.isConnectedGraph(selectedNodeIds, layoutEdges);

    // Diagnostic: log input positions and sizes
    const posSnap = layoutNodes.slice(0, 5).map((n) => {
      const b = n.shape.bounds;
      return `${n.id.slice(0, 8)}:(${b.x.toFixed(0)},${b.y.toFixed(0)} ${b.width.toFixed(0)}x${b.height.toFixed(0)})`;
    });

    // Determine effective algorithm
    const effectiveAlgorithm =
      algorithm === "auto" ? (isConnected ? "force2" : "grid") : algorithm;

    console.warn(
      "[layout] algorithm:",
      effectiveAlgorithm,
      "edges:",
      layoutEdges.length,
      "first positions:",
      posSnap.join(", "),
    );

    let result;
    try {
      if (effectiveAlgorithm === "sort") {
        // Sort: masonry/gallery-wall arrangement
        log?.("layout", `sort: ${layoutNodes.length} nodes`);

        result = sort(
          { nodes: layoutNodes, edges: layoutEdges },
          { anchor: "minimizeMovement" },
        );
      } else if (effectiveAlgorithm === "force2") {
        // Force2: force-directed layout with obstacle avoidance
        const obstacles: LayoutNode[] = [];
        for (const node of allNodes) {
          if (selectedNodeSet.has(node.id)) continue;
          const size = getNodeSize(node);
          obstacles.push({
            id: node.id,
            shape: {
              type: "rect",
              bounds: {
                x: node.position.x,
                y: node.position.y,
                width: size.width,
                height: size.height,
              },
            },
          });
        }

        const obstacleEdges: LayoutEdge[] = [];
        for (const edge of allEdges) {
          const srcIn = selectedNodeSet.has(edge.sourceNodeId);
          const tgtIn = selectedNodeSet.has(edge.targetNodeId);
          if (srcIn !== tgtIn) {
            obstacleEdges.push({
              source: edge.sourceNodeId,
              target: edge.targetNodeId,
            });
          }
        }

        log?.(
          "layout",
          `force2: ${layoutNodes.length} nodes, ${layoutEdges.length} edges, ${obstacles.length} obstacles`,
        );

        result = force2(
          {
            nodes: layoutNodes,
            edges: layoutEdges,
            obstacles: obstacles.length > 0 ? obstacles : undefined,
            obstacleEdges: obstacleEdges.length > 0 ? obstacleEdges : undefined,
          },
          { minDistance: 20, ...options },
        );
      } else if (effectiveAlgorithm === "flow") {
        // Flow: DAG rank-based layout
        log?.(
          "layout",
          `flow: ${layoutNodes.length} nodes, ${layoutEdges.length} edges`,
        );

        result = flow({ nodes: layoutNodes, edges: layoutEdges });
      } else if (effectiveAlgorithm === "tree") {
        // Tree: hierarchical tree layout
        log?.(
          "layout",
          `tree: ${layoutNodes.length} nodes, ${layoutEdges.length} edges`,
        );

        result = tree({ nodes: layoutNodes, edges: layoutEdges });
      } else {
        // Grid: clean arrangement for disconnected graphs
        log?.("layout", `grid: ${layoutNodes.length} nodes`);

        result = grid(
          { nodes: layoutNodes, edges: layoutEdges },
          { spacing: { x: 40, y: 40 }, anchor: "minimizeMovement" },
        );
      }
    } catch (err) {
      console.error("[layout] Algorithm threw:", err);
      return;
    }

    // Disable grid snapping for layout moves — small offsets would snap to zero
    const wasSnapping = this._gridSnapping;
    this._gridSnapping = false;

    // Apply results: compute per-node offset and move
    const movedIds: string[] = [];
    for (const resultNode of result.nodes) {
      const node = allNodes.find((n) => n.id === resultNode.id);
      if (!node) continue;
      const dx = resultNode.x - node.position.x;
      const dy = resultNode.y - node.position.y;
      if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
        moveNodes([resultNode.id], { x: dx, y: dy });
        movedIds.push(resultNode.id);
      }
    }

    // Restore grid snapping
    this._gridSnapping = wasSnapping;

    // Re-render canvas with new positions
    if (movedIds.length > 0) {
      this.render();
    }

    // Notify that state should be re-rendered (positions changed outside of drag)
    this.options.onViewStateChange?.(this.state);

    // Notify caller to persist positions
    if (movedIds.length > 0) {
      this.options.onLayoutComplete?.(movedIds);

      // Unified onChange for layout
      if (this.options.onChange) {
        const currentNodes = this._getNodes();
        const movedNodes = movedIds
          .map((id) => {
            const n = currentNodes.find((node) => node.id === id);
            return n
              ? { id: n.id, position: { x: n.position.x, y: n.position.y } }
              : null;
          })
          .filter((n): n is NonNullable<typeof n> => n !== null);
        if (movedNodes.length > 0) {
          this.options.onChange({ type: "layout", movedNodes });
        }
      }
    }

    log?.("layout", "done");
  }

  /**
   * Align or distribute the selected nodes.
   *
   * Align types: 'left', 'center', 'right', 'top', 'middle', 'bottom'
   * Distribute types: 'horizontal', 'vertical'
   */
  alignSelection(operation: AlignType | DistributeType): void {
    const allNodes = this._getNodes();
    const { log, moveNodes } = this.options;
    const getActualNodeSize =
      this.options.getActualNodeSize ??
      ((nodeId: string) => this._getNodes().find((n) => n.id === nodeId)?.size);

    // Only operate on selected nodes (no fallback to all)
    const nodeIdSet = new Set(allNodes.map((n) => n.id));
    const selectedNodeIds = this.getSelection().filter((id) =>
      nodeIdSet.has(id),
    );

    const isDistribute = operation === "horizontal" || operation === "vertical";
    const minRequired = isDistribute ? 3 : 2;

    if (selectedNodeIds.length < minRequired) {
      log?.(
        "align",
        `skipped: need ${minRequired}+ selected nodes for ${operation}`,
      );
      return;
    }

    const selectedNodeSet = new Set(selectedNodeIds);

    const getNodeSize = (node: EditorNode) => {
      const actual = getActualNodeSize?.(node.id);
      return actual ?? node.size;
    };

    // Build layout nodes from selection
    const layoutNodes: LayoutNode[] = [];
    for (const node of allNodes) {
      if (!selectedNodeSet.has(node.id)) continue;
      const size = getNodeSize(node);
      layoutNodes.push({
        id: node.id,
        shape: {
          type: "rect",
          bounds: {
            x: node.position.x,
            y: node.position.y,
            width: size.width,
            height: size.height,
          },
        },
      });
    }

    const input = { nodes: layoutNodes, edges: [] };
    const result = isDistribute
      ? distribute(input, operation as DistributeType)
      : align(input, operation as AlignType);

    // Disable grid snapping for alignment moves
    const wasSnapping = this._gridSnapping;
    this._gridSnapping = false;

    const movedIds: string[] = [];
    for (const resultNode of result.nodes) {
      const node = allNodes.find((n) => n.id === resultNode.id);
      if (!node) continue;
      const dx = resultNode.x - node.position.x;
      const dy = resultNode.y - node.position.y;
      if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
        moveNodes([resultNode.id], { x: dx, y: dy });
        movedIds.push(resultNode.id);
      }
    }

    this._gridSnapping = wasSnapping;

    if (movedIds.length > 0) {
      this.render();
    }

    this.options.onViewStateChange?.(this.state);

    if (movedIds.length > 0) {
      this.options.onLayoutComplete?.(movedIds);

      // Unified onChange for alignment
      if (this.options.onChange) {
        const currentNodes = this._getNodes();
        const movedNodes = movedIds
          .map((id) => {
            const n = currentNodes.find((node) => node.id === id);
            return n
              ? { id: n.id, position: { x: n.position.x, y: n.position.y } }
              : null;
          })
          .filter((n): n is NonNullable<typeof n> => n !== null);
        if (movedNodes.length > 0) {
          this.options.onChange({ type: "layout", movedNodes });
        }
      }
    }

    log?.("align", `${operation}: moved ${movedIds.length} nodes`);
  }

  /** Check if a set of nodes forms a connected graph via the given edges. */
  private isConnectedGraph(nodeIds: string[], edges: LayoutEdge[]): boolean {
    if (nodeIds.length <= 1) return true;
    if (edges.length === 0) return false;

    // Build adjacency list
    const adj = new Map<string, string[]>();
    for (const id of nodeIds) adj.set(id, []);
    for (const edge of edges) {
      adj.get(edge.source)?.push(edge.target);
      adj.get(edge.target)?.push(edge.source);
    }

    // BFS from first node
    const visited = new Set<string>();
    const queue = [nodeIds[0]];
    visited.add(nodeIds[0]);
    while (queue.length > 0) {
      const current = queue.pop()!;
      for (const neighbor of adj.get(current) ?? []) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      }
    }

    return visited.size === nodeIds.length;
  }

  // ===========================================================================
  // Lifecycle
  // ===========================================================================

  /** Clean up all resources */
  destroy(): void {
    // Cleanup camera animator and automation
    this._cameraAnimator?.destroy();
    this._cameraAnimator = null;
    this._cameraAutomation?.destroy();
    this._cameraAutomation = null;

    // Cleanup behaviors
    for (const cleanup of this.behaviorCleanup) {
      cleanup();
    }
    this.behaviorCleanup.length = 0;

    // Cleanup connect behavior (tracked separately)
    this.unregisterConnectBehavior();

    // Cleanup tool manager
    this.toolManager.destroy();

    // Cleanup editing manager
    this._editingManager?.destroy();

    // Cleanup overlays
    this._overlayManager?.destroy();

    // Reset cursor
    this._cursorManager?.reset();

    // Disconnect input
    this.disconnectInput();

    // Destroy owned canvas
    if (this._ownsCanvas && this._canvas) {
      this._canvas.destroy();
    }
  }
}
